import React from 'react';
import { useTranslation } from "react-i18next";
import DefaultFooter from "../components/default-footer";
import DefaultHeader from "../components/default-header";

const TermCondition = () => {
  const { t } = useTranslation();

  return (
    <>
      <style>{`
        /* ── TERMS PAGE STYLES ── */
        .terms-wrap {
          background: #fff;
          border-radius: 20px;
          padding: 40px 44px;
          box-shadow: 0 8px 48px rgba(41,75,90,0.10);
        }
        .terms-title {
          font-size: 28px;
          font-weight: 700;
          color: #1a2f38;
          margin-bottom: 8px;
        }
        .terms-subtitle {
          font-size: 14px;
          color: #8aa5b0;
          margin-bottom: 32px;
        }
        .terms-divider {
          height: 2px;
          background: linear-gradient(90deg, #294b5a 0%, #D3BE76 100%);
          border-radius: 2px;
          margin-bottom: 28px;
          width: 80px;
        }
        .terms-section {
          margin-bottom: 32px;
        }
        .terms-section:last-child {
          margin-bottom: 0;
        }
        .terms-section-title {
          font-size: 18px;
          font-weight: 700;
          color: #1a2f38;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .terms-section-title .badge {
          font-size: 10px;
          font-weight: 600;
          color: #fff;
          background: #294b5a;
          padding: 2px 10px;
          border-radius: 20px;
          letter-spacing: 0.5px;
        }
        .terms-section p {
          font-size: 14px;
          line-height: 1.8;
          color: #3d5a66;
          margin-bottom: 10px;
        }
        .terms-section ul {
          list-style: none;
          padding: 0;
          margin: 8px 0 0 0;
        }
        .terms-section ul li {
          font-size: 14px;
          line-height: 1.8;
          color: #3d5a66;
          padding-left: 20px;
          position: relative;
          margin-bottom: 4px;
        }
        .terms-section ul li::before {
          content: "•";
          position: absolute;
          left: 0;
          color: #D3BE76;
          font-weight: 700;
        }
        .terms-highlight {
          background: #f7fafc;
          padding: 16px 20px;
          border-radius: 10px;
          border-left: 3px solid #D3BE76;
          margin-top: 6px;
        }
        .terms-highlight p {
          margin-bottom: 0;
        }
        .terms-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 8px;
        }
        .terms-card {
          background: #f7fafc;
          padding: 16px 20px;
          border-radius: 10px;
          border-left: 3px solid #294b5a;
        }
        .terms-card h4 {
          font-size: 14px;
          font-weight: 700;
          color: #1a2f38;
          margin-bottom: 4px;
        }
        .terms-card p {
          font-size: 13px;
          color: #3d5a66;
          margin-bottom: 0;
          line-height: 1.6;
        }

        /* ── BACK BUTTON ── */
        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #294b5a;
          text-decoration: none;
          margin-bottom: 24px;
          transition: color 0.2s;
        }
        .back-link:hover {
          color: #D3BE76;
        }
        .back-link svg {
          width: 16px;
          height: 16px;
          stroke: currentColor;
          fill: none;
          stroke-width: 2.5;
        }

        @media (max-width: 768px) {
          .terms-wrap { padding: 28px 20px; }
          .terms-title { font-size: 22px; }
          .terms-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="header-margin"></div>
      <DefaultHeader />

      <section style={{ paddingTop: '20px' }}>
        <div className="container">
          <div className="row">
            <div className="col-12">

              {/* ── BACK LINK ── */}
              <a href="/" className="back-link">
                <svg viewBox="0 0 24 24">
                  <line x1="19" y1="12" x2="5" y2="12"/>
                  <polyline points="12 19 5 12 12 5"/>
                </svg>
                {t("backToHome", { defaultValue: "Back to Home" })}
              </a>

              {/* ── TERMS CONTENT ── */}
              <div className="terms-wrap">

                {/* Heading */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                  <div style={{ width: "36px", height: "2px", background: "linear-gradient(90deg,#294b5a,#D3BE76)", borderRadius: "2px" }}></div>
                  <span style={{ fontSize: "11px", fontWeight: 700, color: "#294b5a" }}>{t("legalNotice", { defaultValue: "Legal Notice" })}</span>
                </div>
                <h1 className="terms-title">{t("termsTitle", { defaultValue: "Terms & Conditions" })}</h1>
                <p className="terms-subtitle">{t("termsSubtitle", { defaultValue: "Last updated: January 2026" })}</p>
                <div className="terms-divider"></div>

                {/* ── Section 1: Introduction ── */}
                <div className="terms-section">
                  <div className="terms-section-title">
                    {t("introduction", { defaultValue: "Introduction" })}
                    <span className="badge">{t("general", { defaultValue: "General" })}</span>
                  </div>
                  <p>{t("termsIntro1", { defaultValue: "These terms and conditions govern your use of the silverjarz.com website. By accessing and using the website, the user agrees to comply with the terms and conditions set forth in this legal notice." })}</p>
                  <p>{t("termsIntro2", { defaultValue: "The following terms apply to these terms and conditions, privacy policy, disclaimer notice, and all agreements: The words \"Customer,\" \"You,\" and \"Your\" refer to you, the person logging into this website and agreeing to the company's terms and conditions. The terms \"Company,\" \"We,\" \"Us,\" and \"Our\" refer to our company. The terms \"Party,\" \"Parties,\" and \"We\" refer to both the Customer and ourselves." })}</p>
                  <div className="terms-highlight">
                    <p>{t("termsIntro3", { defaultValue: "All terms refer to the offer, acceptance, and consideration of payment necessary to undertake the process of providing assistance to the Customer in the most appropriate manner for the express purpose of meeting the Customer's needs regarding the provision of the Company's stated services, in accordance with and subject to the applicable law of the OMAN." })}</p>
                  </div>
                </div>

                {/* ── Section 2: Changes to Terms ── */}
                <div className="terms-section">
                  <div className="terms-section-title">
                    {t("changesToTerms", { defaultValue: "Changes to Terms" })}
                    <span className="badge">{t("updated", { defaultValue: "Updated" })}</span>
                  </div>
                  <p>{t("changesText1", { defaultValue: "silverjarz.com reserves the right to change, modify, add, or delete parts of these terms and conditions in whole or in part from time to time. Changes to these terms and conditions become effective immediately upon posting on this website." })}</p>
                  <p>{t("changesText2", { defaultValue: "The user should periodically review these terms and conditions on the website to check for any changes or updates. Continued use of this website after changes or updates are posted constitutes notice of the user's agreement to be bound by and comply with these terms and conditions, including such changes or updates." })}</p>
                  <div className="terms-highlight">
                    <p><strong>{t("important", { defaultValue: "⚠️ Important:" })}</strong> {t("changesText3", { defaultValue: "Using the services of this website without reading the terms and conditions is considered negligence on the part of the user, and any inconvenience arising later is the user's responsibility." })}</p>
                  </div>
                </div>

                {/* ── Section 3: Processing Times ── */}
                <div className="terms-section">
                  <div className="terms-section-title">
                    {t("processingTimes", { defaultValue: "Processing Times" })}
                    <span className="badge">{t("timing", { defaultValue: "Timing" })}</span>
                  </div>
                  <div className="terms-grid">
                    <div className="terms-card">
                      <h4>{t("expressProcessing", { defaultValue: "Express Visa Processing" })}</h4>
                      <p>{t("expressText", { defaultValue: "The fast visa processing time mentioned anywhere on the website is 24 working hours." })}</p>
                      <p style={{ marginTop: "6px", color: "#D3BE76", fontWeight: 600 }}>{t("expressRefund", { defaultValue: "If your express visa is not approved within 24 working hours, the additional fee charged for the express option will be refunded." })}</p>
                    </div>
                    <div className="terms-card">
                      <h4>{t("standardProcessing", { defaultValue: "Standard Processing" })}</h4>
                      <p>{t("standardText", { defaultValue: "The approval time mentioned anywhere on the website is the standard time from the Immigration Department. In rare cases, obtaining approval may take longer." })}</p>
                    </div>
                  </div>
                  <ul>
                    <li>{t("timezoneInfo", { defaultValue: "The timeframe mentioned on the website is according to Gulf Standard Time (GST), and working hours begin at 8:00 AM GST." })}</li>
                    <li>{t("holidayInfo", { defaultValue: "Saturday and Sunday are holidays for the Immigration Department. Even if the customer applies for an express visa on Friday, the Immigration Department will not begin processing until Monday at 8:00 AM GST." })}</li>
                    <li>{t("documentsInfo", { defaultValue: "Travel documents are not accepted for OMAN visa applications. Only ordinary passports are permitted." })}</li>
                  </ul>
                </div>

                {/* ── Section 4: Liability ── */}
                <div className="terms-section">
                  <div className="terms-section-title">
                    {t("liability", { defaultValue: "Liability" })}
                    <span className="badge">{t("disclaimer", { defaultValue: "Disclaimer" })}</span>
                  </div>
                  <p>{t("liabilityText", { defaultValue: "silverjarz.com shall not be held liable for any disruption to your travel caused by any type of hotel booking cancellation or flight cancellation." })}</p>
                </div>

                {/* ── Section 5: Privacy Policy ── */}
                <div className="terms-section">
                  <div className="terms-section-title">
                    {t("privacyPolicy", { defaultValue: "Privacy Policy" })}
                    <span className="badge">{t("dataProtection", { defaultValue: "Data Protection" })}</span>
                  </div>
                  <p>{t("privacyText1", { defaultValue: "silverjarz.com sells OMAN visas online. The private information required to process orders submitted through the e-commerce service, including user personal information, credit card details, and phone numbers, is kept in strict confidence and will not be sold or disclosed to third parties." })}</p>
                  <div className="terms-highlight">
                    <p><strong>{t("creditCard", { defaultValue: "🔒 Credit Card Information:" })}</strong> {t("creditCardText", { defaultValue: "silverjarz.com does not retain credit card details under any circumstances." })}</p>
                  </div>
                  <p style={{ marginTop: "10px" }}>{t("privacyText2", { defaultValue: "Your access to and use of our website is also subject to your acceptance of and compliance with the company's Privacy Policy. By agreeing to the terms and conditions, you are required to disclose the following information to us. We collect name, email address, mobile number, passport details, photograph, and country of residence/nationality." })}</p>
                  <p>{t("privacyText3", { defaultValue: "The information collected may be used to customize our products and services, promotions, advertising, and/or to communicate with you." })}</p>
                </div>

                {/* ── Section 6: Pricing Policy ── */}
                <div className="terms-section">
                  <div className="terms-section-title">
                    {t("pricingPolicy", { defaultValue: "Pricing Policy" })}
                    <span className="badge">{t("pricing", { defaultValue: "Pricing" })}</span>
                  </div>
                  <ul>
                    <li>{t("pricingText1", { defaultValue: "The company reserves the right to amend its prices at any time before accepting an order." })}</li>
                    <li>{t("pricingText2", { defaultValue: "The company may revise quoted prices after receiving an order in the event of any circumstance affecting delivery due to government action, foreign exchange cost increases, or any other matter beyond the company's control." })}</li>
                  </ul>
                </div>

                {/* ── Section 7: Payment Policy ── */}
                <div className="terms-section">
                  <div className="terms-section-title">
                    {t("paymentPolicy", { defaultValue: "Payment Policy" })}
                    <span className="badge">{t("payment", { defaultValue: "Payment" })}</span>
                  </div>
                  <ul>
                    <li>{t("paymentText1", { defaultValue: "Payment can be made through various available payment methods, such as Visa or MasterCard." })}</li>
                    <li>{t("paymentText2", { defaultValue: "Payment cards (credit or debit) are subject to validation and authorization checks by your card issuer. If we do not receive the required authorization, we will not be liable for any delay or non-delivery of your order." })}</li>
                  </ul>
                </div>

                {/* ── Section 8: Refund Policy ── */}
                <div className="terms-section">
                  <div className="terms-section-title">
                    {t("refundPolicy", { defaultValue: "Refund Policy" })}
                    <span className="badge">{t("refunds", { defaultValue: "Refunds" })}</span>
                  </div>
                  <ul>
                    <li>{t("refundText1", { defaultValue: "Visa approval is subject entirely to the discretion of immigration officials." })}</li>
                    <li>{t("refundText2", { defaultValue: "silverjarz.com assumes no liability for the rejection of any visa applications." })}</li>
                    <li>{t("refundText3", { defaultValue: "Once a visa application is submitted, it will be processed immediately." })}</li>
                    <li>{t("refundText4", { defaultValue: "Visa application fees are non-refundable in the event of rejection by the Immigration Department." })}</li>
                    <li>{t("refundText5", { defaultValue: "For cancellation or refund requests, please note that such requests are only accepted if submitted within 3 hours of payment and before the application processing begins. A non-refundable service fee of 6.7% of the total amount will be deducted. The remaining balance will be handled according to the cancellation terms specified by the service providers." })}</li>
                  </ul>
                </div>

                {/* ── Section 9: Express Visa Refund Policy ── */}
                <div className="terms-section">
                  <div className="terms-section-title">
                    {t("expressRefundPolicy", { defaultValue: "Express Visa Refund Policy" })}
                    <span className="badge">{t("express", { defaultValue: "Express" })}</span>
                  </div>
                  <div className="terms-highlight" style={{ borderLeftColor: "#294b5a" }}>
                    <p><strong>{t("expressRefundTitle", { defaultValue: "📋 Express Processing Refund:" })}</strong></p>
                    <p>{t("expressRefundText1", { defaultValue: "If you have applied for a OMAN express visa and your application has not been processed within 36 hours, you will be eligible for a refund of the express processing fee (USD 50)." })}</p>
                    <p>{t("expressRefundText2", { defaultValue: "Please note that this refund applies only to the express processing fee and does not include the standard visa application fee." })}</p>
                    <p style={{ marginTop: "6px", fontWeight: 500 }}>{t("expressRefundContact", { defaultValue: "For refund requests, please contact our support team with your order number." })}</p>
                  </div>
                </div>

                {/* ── Footer Note ── */}
                <div style={{ marginTop: "32px", paddingTop: "20px", borderTop: "1px solid #e8eef1" }}>
                  <p style={{ fontSize: "12px", color: "#94b0bc", textAlign: "center", marginBottom: 0 }}>
                    {t("termsFooter", { defaultValue: "© 2026 silverjarz.com - All rights reserved. These terms and conditions are governed by the laws of the OMAN." })}
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      <DefaultFooter />
    </>
  );
};

export default TermCondition;